import { VisionRoutes } from '@vision/ui/constants';
import { ReportType } from '@vision/ui/enums';
import { NavigationLink } from '@vision/ui/interfaces';
import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router';
import { useAppSelector } from './useAppSelector';
import { useNavigationLinksFlat } from './useNavigationLinksFlat';

interface UseNavigationLinkMatchedProps {
  navigationBarLinks?: NavigationLink[];
}

function matchesSearchParams(href: string, search: string): boolean {
  const expected = new URLSearchParams(href.split('?')[1] ?? '');
  const actual = new URLSearchParams(search);

  return [...expected.entries()].every(([key, value]) => actual.get(key) === value);
}

function matchesLink(link: NavigationLink, pathname: string, search: string, loadedReportType?: ReportType): boolean {
  if (!link.href) {
    return false;
  }

  const path = link.href.split('?')[0];
  const isReportsPath = pathname === VisionRoutes.Reports.href || pathname.startsWith(`${VisionRoutes.Reports.href}/`);
  const requestedReportType = new URLSearchParams(search).get('type');
  const effectiveReportType =
    pathname === VisionRoutes.Reports.href ? requestedReportType : (loadedReportType ?? requestedReportType);

  if (link.id === 'TicketManagerReports') {
    return isReportsPath && effectiveReportType === ReportType.INNER_LOOP;
  }

  if (link.id === 'Reports' && isReportsPath && effectiveReportType === ReportType.INNER_LOOP) {
    return false;
  }

  return Boolean(matchPath({ path, end: link.end ?? false }, pathname)) && matchesSearchParams(link.href, search);
}

export function useNavigationLinkMatched({ navigationBarLinks = [] }: UseNavigationLinkMatchedProps = {}) {
  const location = useLocation();
  const links = useNavigationLinksFlat();
  const loadedReportType = useAppSelector(
    (state) => state.ReportsDetailGraphicState.reportDetail?.type ?? state.ReportsDetailState.reportDetail?.type,
  );

  return useMemo(() => {
    const activeLink = links
      .filter((link) => matchesLink(link, location.pathname, location.search, loadedReportType))
      .sort((left, right) => {
        const querySpecificity = Number(Boolean(right.href?.includes('?'))) - Number(Boolean(left.href?.includes('?')));
        const hierarchySpecificity =
          Number(Boolean(right.parentNavigationLink)) - Number(Boolean(left.parentNavigationLink));
        return querySpecificity || hierarchySpecificity || (right.href?.length ?? 0) - (left.href?.length ?? 0);
      })[0];

    if (!activeLink) {
      return {
        activeLink: null,
        matchedLink: null,
        hasChildItems: false,
        isOnNavigationBar: false,
      };
    }

    const matchedLink =
      activeLink.subLabel && activeLink.items?.length > 0
        ? activeLink
        : (activeLink.parentNavigationLink ?? activeLink);

    return {
      activeLink,
      matchedLink,
      hasChildItems: matchedLink.items?.length > 0,
      isOnNavigationBar: navigationBarLinks.some((item) => item.id === matchedLink.id),
    };
  }, [links, loadedReportType, location.pathname, location.search, navigationBarLinks]);
}
